import { isValid } from 'date-fns';
import {
  AnyObject,
  array,
  date,
  mixed,
  number,
  object,
  string,
  TestContext
} from 'yup';

import { getNumberFromString } from '@utils/amount';
import { translate } from '@utils/locale';
import { getDecimalNumbersOnly } from '@utils/common';

const fileRequiredValidation = (
  value: { file: File },
  context: TestContext<AnyObject>
) => {
  if (context.parent.isPublish) {
    const isPublish = context.parent.isPublish;
    if (isPublish) return !!value?.file;
  }
  return true;
};

export const preIPODealSchema = object({
  //TAB3: PreIPO Details
  preIPOType: string().required(translate('required')),
  preIPOMode: string().required(translate('required')),
  isin: string()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .max(12, translate('deals.descriptions.isinLengthError')),
  preIPOName: string()
    .required('Text, 400 char limit')
    .test('required', 'Text, 400 char limit', val => !!val.trim()),

  startDate: date()
    .nullable()
    .typeError('Invalid Date')
    .when('isPublish', ([isPublish], schema) => {
      return isPublish ? schema.required('Enter Date') : schema.optional();
    }),
  endDate: date()
    .nullable()
    .typeError('Invalid Date')
    .test(
      'dateValidation',
      'End date cannot be less than start date',
      (value, { parent }) => {
        if (parent.startDate && isValid(parent.startDate) && value) {
          if (!isNaN(parent.startDate.getTime())) {
            return new Date(value) >= new Date(parent.startDate);
          }
          return false;
        }
        return true;
      }
    )
    .when('isPublish', ([isPublish], schema) => {
      return isPublish ? schema.required('Enter Date') : schema.optional();
    }),
  minInvestment: string()
    .required()
    .test(
      'minLessThanMax',
      'Minimum investment must be less than maximum investment',
      (value, { parent }) => {
        const min = parseInt(getNumberFromString(value ?? ''));
        const max = parseInt(getNumberFromString(parent.maxInvestment));

        if (value && parent.maxInvestment) {
          return min < max;
        }
        return true;
      }
    ),
  maxInvestment: string()
    .required()
    .test(
      'maxGreaterThanMin',
      'Maximum investment must be greater than minimum investment',
      (value, { parent }) => {
        const max = parseInt(getNumberFromString(value ?? ''));
        const min = parseInt(getNumberFromString(parent.minInvestment));

        if (value && parent.minInvestment) {
          return max > min;
        }
        return true;
      }
    ),
  preIPOHighlights: array().of(
    object().shape({
      title: string()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .max(40, 'Enter upto 40 characters for highlight title'),
      description: string()
        .max(800, 'Enter upto 800 characters for highlight')
        .test('requiredValidation', translate('required'), (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const highlights = context.from[0]?.value;
            if (highlights.title) return !!value?.trim();
          }
          return true;
        })
    })
  ),
  coverImage: mixed().test(
    'requiredValidation',
    '2:1 aspect ratio (PNG,JPG,GIF),Max 3 MB',
    fileRequiredValidation
  ),
  preIPODocuments: array().of(
    object().shape({
      title: string().test(
        'requiredValidation',
        translate('required'),
        (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const document = context.from[0]?.value;
            if (document.description || document.document?.file)
              return !!value?.trim();
          }
          return true;
        }
      ),
      description: string().test(
        'requiredValidation',
        translate('required'),
        (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const document = context.from[0]?.value;
            if (document.title || document.document?.file)
              return !!value?.trim();
          }
          return true;
        }
      ),
      document: mixed().test(
        'requiredValidation',
        translate('required'),
        (value: { file: File }, context: TestContext<AnyObject>) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const document = context.from[0]?.value;
            if (document.title || document.description) return !!value?.file;
          }
          return true;
        }
      )
    })
  ),
  preIPOContent: array().of(
    object().shape({
      title: string().test(
        'requiredValidation',
        translate('required'),
        (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const content = context.from[0]?.value;
            if (content.description || content.url) return !!value?.trim();
          }
          return true;
        }
      ),
      description: string().test(
        'requiredValidation',
        translate('required'),
        (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const content = context.from[0]?.value;
            if (content.title || content.url) return !!value?.trim();
          }
          return true;
        }
      ),
      url: string()
        .url('Invalid Url')
        .test('requiredValidation', translate('required'), (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const content = context.from[0]?.value;
            if (content.title || content.description) return !!value?.trim();
          }
          return true;
        })
    })
  ),
  faq: array().of(
    object().shape({
      title: string()
        .max(250, 'Enter upto 250 characters for faq title')
        .test('requiredValidation', translate('required'), (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const faq = context.from[0]?.value;
            if (faq.questions?.[0]?.question) return !!value?.trim();
          }
          return true;
        }),
      questions: array().of(
        object().shape({
          question: string()
            .max(1000, 'Enter upto 1000 characters for question')
            .test(
              'requiredValidation',
              translate('required'),
              (value, context) => {
                if (context.from && context.from[1] && context.from[1].value) {
                  const question = context.from[0]?.value;
                  const faq = context.from[1]?.value;
                  if (question.data || faq.title) return !!value?.trim();
                }
                return true;
              }
            ),
          answer: string()
            .max(1000, 'Enter upto 1000 characters for answer')
            .test(
              'requiredValidation',
              translate('required'),
              (value, context) => {
                if (context.from && context.from[1] && context.from[1].value) {
                  const question = context.from[0]?.value;
                  const faq = context.from[1]?.value;
                  if (question.subheading || faq.title) return !!value?.trim();
                }
                return true;
              }
            )
        })
      )
    })
  ),
  news: array().of(
    object().shape({
      url: string().url('Invalid Url'),
      order: number()
    })
  ),
  sharePriceDate: date()
    .nullable()
    .test('required', 'date required', (value, context) => {
      if (context.from && context.from[0]?.value) {
        const data = context.from[0]?.value;

        if (
          data?.isSharePriceAvailable &&
          context.from[0]?.value.status === '' &&
          (context.from[0]?.value.isPublish || context.from[0]?.value.isDraft)
        ) {
          return !!value;
        }
        return true;
      }
      return true;
    }),
  chargesAndFees: array().of(
    object().shape({
      upperLimit: string()
        .nullable()
        .test('required', 'mismatch', (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const isPublish = context.from[1].value.isPublish;
            const isDraft = context.from[1].value.isDraft;
            const maxInvestment = context.from[1].value.maxInvestment;
            const currentIndex = context.path.match(/\d+/)?.[0];
            const totalLength = context.from[1].value.chargesAndFees?.length;
            if (
              currentIndex &&
              totalLength &&
              parseInt(currentIndex) === totalLength - 1
            ) {
              if (
                (isPublish || isDraft) &&
                parseInt(getNumberFromString(value ?? '')) !==
                  parseInt(getNumberFromString(maxInvestment)) &&
                context.from[1]?.value.status === ''
              ) {
                return false;
              }
            }
            return true;
          }
          return true;
        }),
      sharePrice: string()
        .nullable()
        .test('greater-than-zero', 'error', (value, context) => {
          if (context.from && context.from[1] && context.from[1].value) {
            const isPublish = context.from[1].value.isPublish;
            const isDraft = context.from[1].value.isDraft;
            if (
              (isPublish || isDraft) &&
              Number(getDecimalNumbersOnly(value ?? '')) <= 0
            )
              return false;
          }
          return true;
        })
    })
  )
});
export const preIPOCompanyDetailsSchema = object({});
